import moment from 'moment';
import i18next from 'i18next';
import NumberParser from 'utils/parsers/NumberParser';
import Personalizer from 'personalizer';
import NumberSeparators from 'utils/personalizer/NumberSeparators';
import NumberFormats from 'utils/personalizer/NumberFormats';
import DateFormatter from 'utils/date/DateFormatter';
import formatNumber from 'utils/NumberFormatter';
import dayjs from 'dayjs';
import dayJsDuration from 'dayjs/plugin/duration';
dayjs.extend(dayJsDuration);

i18next.loadNamespaces(['challengewidget', 'units', 'common']);

var TimeUtil = {
    secondsToTime: function(secs) {
        var hours = Math.floor(secs / (60 * 60));

        var divisor_for_minutes = secs % (60 * 60);
        var minutes = Math.floor(divisor_for_minutes / 60);

        var divisor_for_seconds = divisor_for_minutes % 60;
        var seconds = Math.ceil(divisor_for_seconds);

        if (seconds >= 60) {
            minutes++;
            seconds -= 60;
        }

        if (minutes >= 60) {
            hours++;
            minutes -= 60;
        }

        var obj = {
            h: hours,
            m: minutes,
            s: seconds
        };
        return obj;
    },

    /**
     * Converts number of seconds to number of minutes
     * e.g: 60 -> 1
     * @param {number} secs - number of seconds
     * @return {number} - number of minutes
     */
    secondsToMinutes: function(secs) {
        return Math.floor(secs / 60);
    },

    formatSeconds: function(timeInSeconds) {
        var time = this.secondsToTime(timeInSeconds);
        time = (time.h > 0 ? TimeUtil.pad2(time.h) + ':' : '') + TimeUtil.pad2(time.m) + ':' + TimeUtil.pad2(time.s);
        return time;
    },

    /**
     * Retruns formatted sleep time from int 5.5 to string "5:30"
     * e.g: 5.25 -> 5:15
     * @param  {Float} time
     * @return {String} - formatted time in format hh:mm
     */
    floatToTime: function(time) {
        var hours = Math.floor(time);
        var minutes = (time - hours) * 60;

        return hours + ':' + this.pad2(Math.floor(minutes));
    },
    /**
     * Converts hours and minutes into their float value
     * E.g: 5:15 -> 5.25
     * @param  {Integer} h  - hour
     * @param  {Integer} min - minutes
     * @return {Float}  - the float value for given time
     */
    hrsMinToFloat: function(h, min) {
        var minDec = min / 60;
        return Number((h + minDec).toFixed(2));
    },

    timeObjectToSeconds: function(timeObject) {
        var totalSeconds = 0;
        if (timeObject.h) totalSeconds += Number(timeObject.h * 3600);

        if (timeObject.m) totalSeconds += Number(timeObject.m * 60);

        if (timeObject.s) totalSeconds += Number(timeObject.s);

        return totalSeconds;
    },

    dateTimeToSeconds: function(d) {
        var totalSeconds = d.getSeconds() + d.getMinutes() * 60 + d.getHours() * 3600;
        return totalSeconds;
    },

    // Short display is just Hour + a/p for am/pm designation
    formatTime12Hour: function(secs, shortDisplay, excludeAmPm) {
        var time = this.secondsToTime(secs);
        var hour = time.h;
        var minutes = time.m;
        var amPm = i18next.t('common:AM');

        if (hour === 0 || hour === 12 || hour === 24) {
            amPm = hour === 12 ? i18next.t('common:PM') : i18next.t('common:AM');
            hour = 12;
        } else {
            amPm = hour > 12 ? i18next.t('common:PM') : i18next.t('common:AM');
            hour = hour % 12;
        }

        amPm = excludeAmPm ? '' : amPm;
        return (shortDisplay
            ? hour + amPm.substr(0, 1).toLowerCase()
            : hour + ':' + this.pad2(minutes) + ' ' + amPm
        ).trim();
    },

    // Short display is just Hour + AM/PM
    // E.g.: 8 AM
    formatShortTime: function(secs) {
        var time = this.secondsToTime(secs);
        var hour = time.h;
        var amPm = i18next.t('common:AM');

        if (hour === 0 || hour === 12 || hour === 24) {
            amPm = hour === 12 ? i18next.t('common:PM') : i18next.t('common:AM');
            hour = 12;
        } else {
            amPm = hour > 12 ? i18next.t('common:PM') : i18next.t('common:AM');
            hour = hour % 12;
        }

        return hour + ' ' + amPm;
    },

    /**
     * Returns short hour 12 hrs format from date format
     * E.g: 20:15 (in date format) -> 8
     * @param  {Date} date
     * @return {Integer} short hour
     */
    getShortHourFromDate: function(date) {
        var hour = date.getHours();
        switch (hour) {
            case 0:
            case 24:
                return 0;
            case 12:
                return 12;
            default:
                return hour % 12;
        }
    },

    // Short display is just hour
    formatTime24Hour: function(secs, shortDisplay, padHours) {
        var time = this.secondsToTime(secs);
        var hour = time.h;
        var minutes = time.m;

        if (hour === 24) {
            // wrapped over to midnight the next day, display as 0:00
            hour = 0;
        }

        var hourFormat = padHours ? this.pad2(hour) : hour;
        return shortDisplay ? hourFormat + '' : hourFormat + ':' + this.pad2(minutes);
    },

    pad2: function(val) {
        var str = '' + val;
        if (str.length >= 2) {
            return str;
        } else if (str.length === 1) {
            return '0' + str;
        } else {
            return '00';
        }
    },

    formatTime: function(secs, format24Hour, shortDisplay, excludeAmPm) {
        if (format24Hour) {
            return this.formatTime24Hour(secs, shortDisplay);
        } else {
            return this.formatTime12Hour(secs, shortDisplay, excludeAmPm);
        }
    },

    // secs = seconds of time since midnight
    // options:
    // {
    //   format24Hour: bool (format the timestamp as 24 hour time)
    //   shortDisplay: bool (remove minutes AND switch AM/PM -> a/p)
    //   excludeAmPm: bool (excludes AM/PM for 12 hour times)
    //   padHours: bool (adds leading 0 to hours if less than '10:xx' - note, only 24 hour time supports this)
    //   hideMinutes: bool (only remove the ':xx' for minutes, but leave any AM/PM labeling)
    // }
    formatTimeDetailed: function(secs, options) {
        if (!options) return this.formatTime12Hour(secs);

        let time;
        if (options.format24Hour) {
            time = this.formatTime24Hour(secs, options.shortDisplay, options.padHours);
        } else {
            time = this.formatTime12Hour(secs, options.shortDisplay, options.excludeAmPm);
        }

        if (!options.shortDisplay && options.hideMinutes) {
            const pieces = time.split(/[: ]/);
            // Keep AM/PM if it was in the original returned formatted time string
            const amPm = pieces.length > 2 ? ` ${pieces[2]}` : '';
            return `${pieces[0]}${amPm}`;
        }

        return time;
    },

    formatDescriptiveDuration: function(secs, shortMinLabel, includeMinutesIfZero = true) {
        var durationObject = TimeUtil.secondsToTime(secs),
            retVal = '--';
        var minuteLabel = shortMinLabel ? 'duration_minute_short' : 'duration_minute';

        if (durationObject.h !== 0 && !isNaN(durationObject.h)) {
            retVal = durationObject.h + i18next.t('units:duration_hour_short');
            if (durationObject.m === 0) {
                if (includeMinutesIfZero) {
                    retVal += ' ' + durationObject.m + i18next.t('units:' + minuteLabel);
                }
            } else {
                retVal += ' ' + durationObject.m + i18next.t('units:' + minuteLabel);
            }
        } else if (durationObject.m !== 0 && !isNaN(durationObject.m)) {
            retVal = durationObject.m + i18next.t('units:' + minuteLabel);
        }

        return retVal;
    },

    timeToMinutes: function(timeString) {
        // format hh:mm:ss or mm:ss
        var parts = timeString.split(':');
        if (parts.length != 2 && parts.length != 3) {
            throw new Error('Invalid format! Can only be hh:mm:ss or mm:ss');
        }
        var hours = parseInt(parts.length === 2 ? 0 : parts[0]);
        var minutes = parseInt(parts.length === 2 ? parts[0] : parts[1]);
        var seconds = parseInt(parts.length === 2 ? parts[1] : parts[2]);
        var result = hours * 60 + minutes + seconds * (1 / 60);
        //result = Math.round(result * 10) / 10;
        return result;
    },

    /*
     *  This function converts a format of mm:ss or hh:mm:ss to a number of seconds
     *  @param timeString - a string in the format of mm:ss or hh:mm:ss to convert to seconds
     *  @returns int representing the number of seconds for a given mm:ss or hh:mm:ss string
     */
    hoursMinutesAndSecondsToSeconds: function(timeString) {
        var parts = timeString.trim().split(/:| /);
        var numberParser = new NumberParser({
            numberSeparator: Personalizer.getNumberSeparator()
        });
        var otherSeparator = Personalizer.getNumberSeparator() === NumberSeparators.DECIMAL_PERIOD ? ',' : '.';

        if (timeString.indexOf(otherSeparator) >= 0) {
            throw new Error('Invalid Format');
        }

        // switch to number parser so , number separator users can enter partial seconds
        try {
            for (var i = 0; i < parts.length; i++) {
                parts[i] = numberParser.parse(parts[i]);
            }
        } catch (e) {
            throw new Error('Invalid Format');
        }

        var hours = parts.length === 3 ? parts[0] : 0; // hours is always first or not included
        var minutes = parts.length > 2 ? +parts[1] : +parts[0]; // minutes is either 2nd(if hours), or 1st
        var seconds = +parts[parts.length - 1]; // seconds is always last

        return hours * 3600 + minutes * 60 + seconds;
    },

    /*
     *  This function converts a time of day to a number of seconds elapsed since midnight
     *  @param timeString - a string in the format of hh:ss A to convert to seconds since midnight
     *  @returns int representing the number of seconds for a time since midnight
     */
    timeToSeconds: function(timeString) {
        var hour = 0;
        var minutes = 0;
        var amPm = '';
        var result = 0;
        var parts = [];

        parts = timeString.trim().split(/:| /);

        /*Validation*/
        if (isNaN(parts[0]) || isNaN(parts[1])) {
            throw new Error('Invalid Format');
        }

        if (parts.length > 2) {
            if (parts.length === 3 && parts[2] !== i18next.t('common:AM') && parts[2] !== i18next.t('common:PM')) {
                throw new Error('Invalid Format');
            } else if (
                parts.length === 4 &&
                parts[2] + ' ' + parts[3] !== i18next.t('common:AM') &&
                parts[2] + ' ' + parts[3] !== i18next.t('common:PM')
            ) {
                throw new Error('Invalid Format');
            }
        }
        /*End Validation*/

        hour = Number(parts[0]);
        minutes = Number(parts[1]);
        result = hour * 3600 + minutes * 60;

        if (parts.length > 2) {
            amPm = parts.length === 3 ? parts[2] : parts[2] + ' ' + parts[3];
            result += hour !== 12 && amPm === i18next.t('common:PM') ? 43200 : 0;
            result -= hour === 12 && amPm === i18next.t('common:AM') ? 43200 : 0;
        }

        return result;
    },

    /**
     * convert 24 hour time to seconds
     * @param  {String} timeString  - 24 hour time string
     * @param  {Boolean} hoursFirst - it decides how to convert time
     * @return {Integer} - seconds
     */
    time24ToSeconds: function(timeString, hoursFirst) {
        let parts = timeString.trim().split(/:| /);
        let hours = 0,
            minutes = 0,
            seconds = 0;
        if (hoursFirst) {
            // case 1. timeString = hh:mm:ss
            // case 2. timeString = hh:mm
            // case 3. timeString = hh
            hours = +parts[0];
            minutes = parts.length >= 2 ? +parts[1] : 0;
            seconds = parts.length === 3 ? +parts[2] : 0;
        } else {
            // case 1. timeString = hh:mm:ss
            // case 2. timeString = mm:ss
            // case 3. timeString = ss
            parts.reverse();
            seconds = +parts[0];
            minutes = parts.length >= 2 ? +parts[1] : 0;
            hours = parts.length === 3 ? +parts[2] : 0;
        }

        return 3600 * hours + 60 * minutes + seconds;
    },

    timeDifferenceRoundingUp: function(fromDate, toDate) {
        fromDate = moment(fromDate);
        toDate = moment(toDate);
        var duration = this.durationBetweenDates(fromDate, toDate);
        if (duration === 0 || duration === null) {
            return null;
        }
        if (duration.years() > 0) {
            return DateFormatter.formatMonthDayYear(toDate.toDate());
        } else if (duration.months() > 0) {
            var months = duration.months();
            if (duration.weeks() > 0) {
                months++;
            }
            if (months === 1) {
                return i18next.t('common:one_month');
            } else {
                return i18next.t('common:months', { 0: months });
            }
        } else if (duration.weeks() > 0) {
            var weeks = duration.weeks();
            if (duration.days() % 7 > 0) {
                weeks++;
            }
            if (weeks === 1) {
                return i18next.t('common:one_week');
            } else {
                return i18next.t('common:weeks', { 0: weeks });
            }
        } else if (duration.days() > 0) {
            var days = duration.days();
            if (duration.hours() > 0) {
                days++;
            }
            if (days === 1) {
                return i18next.t('challengewidget:one.day');
            } else {
                return i18next.t('challengewidget:days', { 0: days });
            }
        } else {
            duration.add(1, 'minutes');
            if (duration.days() === 1) {
                // If we have 1 day until the challenge we display 24:00
                return '24:00';
            }
            return (
                formatNumber(duration.hours(), NumberFormats.ONE_OR_TWO_DIGITS) +
                ':' +
                formatNumber(duration.minutes(), NumberFormats.TWO_DIGITS)
            );
        }
    },

    /**
     * return time difference without rounding up.
     *
     * Format guidelines
     *  - over 1 year                   = Jan 1 2004
     *  - 1 month   ~ 11 months         = 11 months
     *  - 1 week    ~ 3 weeks           = 3 weeks
     *  - 1 day     ~ 6 days            = 6 days
     *  - less 1 day                    = 23:59
     *
     * @param fromDate
     * @param toDate
     * @returns
     */
    timeDifference: function(fromDate, toDate) {
        const fromDateDayJs = dayjs(fromDate);
        const toDateDayJs = dayjs(toDate);
        const duration = dayjs.duration(toDateDayJs.diff(fromDateDayJs));
        if (!duration || duration.asMilliseconds() <= 0) {
            return null;
        }
        if (duration.years() > 0) {
            return DateFormatter.formatMonthDayYear(toDate.toDate());
        } else if (duration.months() > 0) {
            const months = duration.months();
            if (months === 1) {
                return i18next.t('common:one_month');
            } else {
                return i18next.t('common:months', { 0: months });
            }
        } else if (duration.weeks() > 0) {
            const weeks = duration.weeks();
            if (weeks === 1) {
                return i18next.t('common:one_week');
            } else {
                return i18next.t('common:weeks', { 0: weeks });
            }
        } else if (duration.days() > 0) {
            const days = duration.days();
            if (days === 1) {
                return i18next.t('challengewidget:one.day');
            } else {
                return i18next.t('challengewidget:days', { 0: days });
            }
        } else {
            return (
                formatNumber(duration.hours(), NumberFormats.ONE_OR_TWO_DIGITS) +
                ':' +
                formatNumber(duration.minutes(), NumberFormats.TWO_DIGITS)
            );
        }
    },

    durationBetweenDates: function(fromDate, toDate) {
        var fromDate = moment(fromDate);
        var toDate = moment(toDate);
        var diffMs = toDate.diff(fromDate);
        if (diffMs < 0) {
            return 0;
        }
        return moment.duration(diffMs);
    },

    secondsToHoursAndMinutes: function(seconds, padHours = false, padMinutes = false) {
        // Make sure seconds is a number
        if (seconds === '' || isNaN(seconds)) {
            return '';
        }

        const { h, m } = this.secondsToTime(seconds);
        return (padHours ? this.pad2(h) : h) + ':' + (padMinutes ? this.pad2(m) : m);
    },

    secondsToMinutesAndSeconds: function(seconds, padMinutes = false, padSeconds = false) {
        // Make sure seconds is a number and less than an hour
        if (seconds === '' || isNaN(seconds) || seconds >= 3600) {
            return '';
        }

        const { m, s } = this.secondsToTime(seconds);
        return (padMinutes ? this.pad2(m) : m) + ':' + (padSeconds ? this.pad2(s) : s);
    },

    secondsToHoursMinutesAndSeconds: function(seconds) {
        // Make sure seconds is a number
        if (seconds === '' || isNaN(seconds)) {
            return '00:00:00';
        }

        const { h, m, s } = this.secondsToTime(seconds);
        return `${formatNumber(h, NumberFormats.NO_DECIMAL_PAD_2_ZEROS)}:${formatNumber(
            m,
            NumberFormats.NO_DECIMAL_PAD_2_ZEROS
        )}:${formatNumber(s, NumberFormats.NO_DECIMAL_PAD_2_ZEROS)}`;
    },

    durationToFormattedHoursAndMinutes: function(fromDate, toDate) {
        const ms = this.durationBetweenDates(fromDate, toDate);
        const seconds = Math.floor(ms / 1000);

        const { h, m } = this.secondsToTime(seconds);
        return `${h}${i18next.t('units:duration_hour')} ${m}${i18next.t('units:duration_minute')}`;
    },

    /**
     * Returns a formatted timed object.
     * Example: let timeObject = { h: "2", m: "1", s: "1" } would return 02:01:01
     * @param {} timeObject
     * @returns
     */
    formatTimeObject(timeObject) {
        timeObject.h = ('' + timeObject.h).padStart(2, 0);
        timeObject.m = ('' + timeObject.m).padStart(2, 0);
        timeObject.s = ('' + timeObject.s).padStart(2, 0);
        return `${timeObject.h}:${timeObject.m}:${timeObject.s}`;
    },

    /**
     * Takes a time string and makes sure it's in hh:mm:ss format
     * @param {*} time
     * @returns
     */
    timeFormatter(time) {
        let formatted = time || '00:00:00'; // default to zero in case a falsy value is passed in
        if (!!time && time.split(':').length > 0) {
            let p = time.split(':');
            if (p.at(-1) === '') {
                p[p.length - 1] = '0';
            }
            // Make sure that we are in hh:mm:ss format
            for (let i = p.length; i < 3; i++) {
                formatted = '00:' + formatted;
            }
            p = formatted.split(':');
            formatted = '';
            // Make sure that the parts are formatted correctly
            for (let i = 0; i < 3; i++) {
                formatted += p[i].padStart(2, '0');
                formatted += i + 1 < 3 ? ':' : '';
            }
        }
        return formatted;
    },

    /**
     * Logic:
     *   Values less than 96hrs round down to nearest hour
     *   Values greater than 96hrs round to days as follows
     *     if 6 <= hours in day < 18 then add a half day to the value
     *     if hours in day > 18 then roud up to the nearest day value
     */
    minutesToHoursOrDaysRange(minutes) {
        const hours = minutes / 60;
        const days = hours / 24;
        const remainderHours = hours % 24;

        let timeStr = '';

        if (hours <= 96) {
            timeStr = Math.floor(hours) + i18next.t('units:duration_hour_short');
        } else if (remainderHours < 6) {
            timeStr = Math.floor(days) + i18next.t('units:duration_day_short');
        } else if (remainderHours >= 6 && remainderHours < 18) {
            timeStr = Math.floor(days) + '.5' + i18next.t('units:duration_day_short');
        } else {
            timeStr = Math.ceil(days) + i18next.t('units:duration_day_short');
        }

        return timeStr;
    },

    /*
     * This function validates the time string.
     *
     */
    validateTimeHHMMString(timeString) {
        let valid = true,
            hours,
            minutes;
        if (!timeString) {
            valid = false;
        } else {
            let parts = timeString.trim().split(/:| /);

            // Example in Spanish: hh:mm a. m.
            if (parts.length > 3) {
                parts = [parts[0], parts[1], parts.slice(2).join(' ')];
            }

            // hh:mm am/pm
            if (parts.length === 3) {
                if (isNaN(parts[0]) || parts[0].length > 2 || isNaN(parts[1]) || parts[1].length > 2) {
                    valid = false;
                }

                if (valid) {
                    if (parts[2] !== i18next.t('common:AM') && parts[2] !== i18next.t('common:PM')) {
                        valid = false;
                    } else {
                        hours = parseInt(parts[0], 10);
                        minutes = parseInt(parts[1], 10);
                        if (hours < 0 || hours > 12) {
                            valid = false;
                        }
                        if (valid && (minutes < 0 || minutes > 59)) {
                            valid = false;
                        }
                    }
                }
            } else if (parts.length === 2) {
                // hh:mm - 24 hour format
                if (isNaN(parts[0]) || isNaN(parts[1])) {
                    valid = false;
                }
                if (valid) {
                    hours = parseInt(parts[0], 10);
                    minutes = parseInt(parts[1], 10);
                    if (hours < 0 || hours > 23) {
                        valid = false;
                    }
                    if (valid && (minutes < 0 || minutes > 59)) {
                        valid = false;
                    }
                }
            } else {
                valid = false;
            }
        }
        return valid;
    },

    formatSecondsToDescriptiveDuration: function(secs, includeMinutesIfZero = false) {
        var durationObject = TimeUtil.secondsToTime(secs),
            retVal = '--';

        if (durationObject.h !== 0 && !isNaN(durationObject.h)) {
            retVal =
                durationObject.h +
                ' ' +
                `${
                    durationObject.h === 1
                        ? i18next.t('units:duration_hour_lowercase')
                        : i18next.t('units:duration_hours_lowercase')
                }`;
            if (durationObject.m === 0) {
                if (includeMinutesIfZero) {
                    retVal += ' ' + durationObject.m + i18next.t('units:duration_minutes_lowercase');
                }
            } else {
                retVal += ' ' + durationObject.m + ' ' + i18next.t('units:duration_minutes_lowercase');
            }
        } else if (durationObject.m !== 0 && !isNaN(durationObject.m)) {
            retVal = durationObject.m + ' ' + i18next.t('units:duration_minutes_lowercase');
        }

        return retVal;
    }
};

export default TimeUtil;
